import { useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/lib/ui";
import { LocateFixed, MapPin, PlayCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useGeoLocate } from "@/components/LocationFilter";
import VerificationCard from "@/components/VerificationCard";
import TrustBadges from "@/components/TrustBadges";
import Avatar, { resizeImage } from "@/components/Avatar";
import { Camera } from "lucide-react";
import FamilyShareCard from "@/components/FamilyShareCard";
import AvailabilityEditor, { emptyAvailability } from "@/components/AvailabilityEditor";
import { Siren } from "lucide-react";
import PinSetup from "@/components/PinSetup";

const inputCls = "w-full px-4 min-h-[52px] rounded-xl border border-[#E2D9CE] focus:border-[#D96B43] focus:ring-2 focus:ring-[#D96B43]/20 outline-none";

export default function Profile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: user.name, phone: user.phone || "", address: user.address || "",
    bio: user.bio || "", skills: (user.skills || []).join(", "),
    lat: user.lat ?? null, lng: user.lng ?? null,
    emergency_contact: { name: "", relation: "", phone: "", email: "", ...(user.emergency_contact || {}) },
    availability: user.availability || emptyAvailability(),
  });
  const setEc = (k) => (e) => setForm({ ...form, emergency_contact: { ...form.emergency_contact, [k]: e.target.value } });
  const [loading, setLoading] = useState(false);
  const { locate, locating } = useGeoLocate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pinHome = async () => {
    const c = await locate();
    if (c) { setForm({ ...form, ...c }); toast.success("Home location captured. Save to keep it."); }
  };

  const refreshUser = () => api.get("/auth/me").then((r) => setUser(r.data));

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose a JPG or PNG photo."); return; }
    try {
      const avatar = await resizeImage(f, 320);
      const { data } = await api.put("/auth/profile", { avatar });
      setUser(data);
      toast.success("Photo updated!");
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Couldn't read that photo."); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean) };
      delete payload.avatar;
      const { data } = await api.put("/auth/profile", payload);
      setUser(data);
      toast.success("Profile updated");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const roleLabel = { seeker: "Care Seeker", helper: "Helper", admin: "Admin" };

  return (
    <div className="max-w-2xl mx-auto px-4 py-10">
      <div className="flex items-center gap-4 mb-8">
        <label className="relative cursor-pointer group" title="Change photo">
          <Avatar user={user} size="lg" color="bg-[#D96B43]" />
          <span className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-[#2E7D6B] text-white flex items-center justify-center shadow group-hover:bg-[#256658]">
            <Camera className="w-5 h-5" />
          </span>
          <input data-testid="profile-photo-input" type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
        </label>
        <div>
          <h1 className="text-3xl font-display font-bold text-slate-900">{user.name}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge color="orange">{roleLabel[user.role]}</Badge>
            <TrustBadges user={user} />
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="bg-white border border-[#E2D9CE] rounded-2xl p-6 sm:p-8 space-y-5 shadow-sm">
        <div>
          <label className="block font-semibold text-slate-800 mb-2">Name</label>
          <input data-testid="profile-name" value={form.name} onChange={set("name")} className={inputCls} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block font-semibold text-slate-800 mb-2">Phone</label>
            <input data-testid="profile-phone" value={form.phone} onChange={set("phone")} className={inputCls} />
          </div>
          <div>
            <label className="block font-semibold text-slate-800 mb-2">Location</label>
            <input data-testid="profile-address" value={form.address} onChange={set("address")} className={inputCls} />
          </div>
        </div>
        <div>
          <label className="block font-semibold text-slate-800 mb-2">Home location (for nearby matching)</label>
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" data-testid="profile-pin-location-btn" onClick={pinHome} disabled={locating}
              className="flex items-center gap-2 bg-[#E8F4F1] hover:bg-[#d6ebe5] text-[#2E7D6B] font-semibold px-4 min-h-[48px] rounded-xl transition-colors disabled:opacity-60">
              <LocateFixed className="w-5 h-5" /> {locating ? "Locating…" : form.lat != null ? "Update my location" : "Use my current location"}
            </button>
            {form.lat != null && (
              <span data-testid="profile-coords" className="flex items-center gap-1 text-slate-600 text-sm">
                <MapPin className="w-4 h-4 text-[#2E7D6B]" /> Pinned ({form.lat.toFixed(3)}, {form.lng.toFixed(3)})
              </span>
            )}
          </div>
        </div>
        <div>
          <label className="block font-semibold text-slate-800 mb-2">About you</label>
          <textarea data-testid="profile-bio" rows={3} value={form.bio} onChange={set("bio")} className={`${inputCls} py-3`} />
        </div>
        {user.role === "helper" && (
          <div>
            <label className="block font-semibold text-slate-800 mb-2">Skills (comma separated)</label>
            <input data-testid="profile-skills" value={form.skills} onChange={set("skills")} placeholder="Driving, Cooking, First aid" className={inputCls} />
          </div>
        )}
        {user.role === "helper" && (
          <div className="pt-4 border-t border-[#E2D9CE]">
            <h2 className="text-xl font-display font-bold text-slate-900 mb-1">When are you available?</h2>
            <p className="text-slate-600 mb-4">Seekers see who can actually come.</p>
            <AvailabilityEditor value={form.availability} onChange={(availability) => setForm({ ...form, availability })} />
          </div>
        )}
        {user.role !== "helper" && (
          <div className="pt-4 border-t border-[#E2D9CE]">
            <div className="flex items-center gap-2 mb-1"><Siren className="w-6 h-6 text-[#D96B43]" /><h2 className="text-xl font-display font-bold text-slate-900">Emergency / family contact</h2></div>
            <p className="text-slate-600 mb-4">We'll let this person know when a helper is booked and when the help is completed.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="block font-semibold text-slate-800 mb-2">Name</label><input data-testid="ec-name" value={form.emergency_contact.name} onChange={setEc("name")} placeholder="e.g. Priya (daughter)" className={inputCls} /></div>
              <div><label className="block font-semibold text-slate-800 mb-2">Relationship</label><input data-testid="ec-relation" value={form.emergency_contact.relation} onChange={setEc("relation")} placeholder="Daughter, son, neighbour…" className={inputCls} /></div>
              <div><label className="block font-semibold text-slate-800 mb-2">Phone</label><input data-testid="ec-phone" value={form.emergency_contact.phone} onChange={setEc("phone")} className={inputCls} /></div>
              <div><label className="block font-semibold text-slate-800 mb-2">Email</label><input data-testid="ec-email" type="email" value={form.emergency_contact.email} onChange={setEc("email")} className={inputCls} /></div>
            </div>
          </div>
        )}
        <button data-testid="profile-save-btn" disabled={loading}
          className="w-full bg-[#D96B43] hover:bg-[#C25832] disabled:opacity-60 text-white font-semibold min-h-[52px] rounded-xl text-lg transition-colors">
          {loading ? "Saving…" : "Save changes"}
        </button>
      </form>
      {user.role === "helper" && <VerificationCard user={user} onUpdated={refreshUser} />}
      {user.role === "seeker" && <FamilyShareCard contactEmail={user.emergency_contact?.email} />}
      <PinSetup user={user} onUpdated={refreshUser} />
      {user.role === "seeker" && (
        <section data-testid="tour-replay" className="bg-white border border-[#E2D9CE] rounded-2xl p-6 sm:p-8 mt-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-display font-bold text-slate-900">Need a reminder of how Kindred works?</h2>
            <p className="text-slate-600">Replay the 3-step spoken walkthrough any time.</p>
          </div>
          <button data-testid="tour-replay-btn" type="button" onClick={() => { localStorage.removeItem(`kindred_tour_done_${user.id}`); navigate("/dashboard"); }}
            className="bg-[#D96B43] hover:bg-[#C25832] text-white text-lg font-semibold px-6 min-h-[56px] rounded-xl flex items-center gap-2 transition-colors"><PlayCircle className="w-6 h-6" /> Show me around again</button>
        </section>
      )}
    </div>
  );
}
